import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Heart, Share2, Leaf, Truck, Shield, Minus, Plus, Upload, X, Move, ZoomIn, RotateCw, RotateCcw, Palette, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [customization, setCustomization] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const { addToCart } = useCart();
  const { token } = useAuth();

  // Logo customization state
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 30 });
  const [logoSize, setLogoSize] = useState([30]);
  const [logoRotation, setLogoRotation] = useState([0]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const previewRef = useRef(null);

  // Function to remove background from image
  const removeBackground = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Detect background color from corners
        const corners = [
          { x: 0, y: 0 },
          { x: canvas.width - 1, y: 0 },
          { x: 0, y: canvas.height - 1 },
          { x: canvas.width - 1, y: canvas.height - 1 }
        ];
        
        let bgR = 0, bgG = 0, bgB = 0;
        corners.forEach(corner => {
          const idx = (corner.y * canvas.width + corner.x) * 4;
          bgR += data[idx];
          bgG += data[idx + 1];
          bgB += data[idx + 2];
        });
        bgR = Math.round(bgR / 4);
        bgG = Math.round(bgG / 4);
        bgB = Math.round(bgB / 4);
        
        // Calculate tolerance based on how "white" the background is
        const brightness = (bgR + bgG + bgB) / 3;
        const tolerance = brightness > 200 ? 50 : brightness > 150 ? 35 : 25;
        
        // Remove similar colors to background
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          const diff = Math.abs(r - bgR) + Math.abs(g - bgG) + Math.abs(b - bgB);
          
          if (diff < tolerance) {
            // Make transparent
            data[i + 3] = 0;
          } else if (diff < tolerance * 2) {
            // Semi-transparent for edges (anti-aliasing)
            data[i + 3] = Math.min(255, (diff - tolerance) * (255 / tolerance));
          }
        }
        
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = imageDataUrl;
    });
  };

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${productId}`);
      setProduct(res.data);
      setQuantity(res.data.min_quantity || 1);
      
      // Fetch related products
      const relatedRes = await axios.get(`${API}/products`, {
        params: { category: res.data.category_id, limit: 4 }
      });
      setRelatedProducts(relatedRes.data.filter(p => p.id !== productId).slice(0, 3));
    } catch (e) {
      console.error("Error fetching product:", e);
      toast.error("Produit non trouvé");
      navigate("/boutique");
    }
    setLoading(false);
  };

  const handleAddToCart = async () => {
    if (!token) {
      toast.error("Connectez-vous pour ajouter au panier");
      return;
    }
    if (quantity < product.min_quantity) {
      toast.error(`Quantité minimum: ${product.min_quantity} unités`);
      return;
    }
    const parts = [];
    if (selectedColor) parts.push(`Couleur: ${selectedColor}`);
    if (selectedSize) parts.push(`Taille: ${selectedSize}`);
    if (uploadedLogo) parts.push(`Logo personnalisé uploadé - Position: ${logoPosition.x}%, ${logoPosition.y}% - Taille: ${logoSize[0]}%`);
    if (customization) parts.push(customization);
    const customText = parts.join(' | ') || null;
    
    const result = await addToCart(product.id, quantity, customText);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(`${product.name} ajouté au panier`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const getUnitPrice = () => {
    if (!product) return 0;
    if (product.pricing_tiers?.length > 0) {
      const tier = product.pricing_tiers.find(t => quantity >= t.min_qty && quantity <= t.max_qty);
      if (tier) return tier.price;
      const sorted = [...product.pricing_tiers].sort((a, b) => a.min_qty - b.min_qty);
      if (quantity < sorted[0]?.min_qty) return sorted[0]?.price || product.price;
      return sorted[sorted.length - 1]?.price || product.price;
    }
    return product.price;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Le fichier est trop volumineux (max 5MB)");
        return;
      }
      
      setIsProcessing(true);
      toast.info("Traitement du logo en cours...");
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const originalImage = event.target?.result;
          // Remove background
          const processedImage = await removeBackground(originalImage);
          setUploadedLogo(processedImage);
          toast.success("Logo traité ! Fond supprimé automatiquement. Déplacez-le sur l'image.");
        } catch (error) {
          console.error("Error processing logo:", error);
          setUploadedLogo(event.target?.result);
          toast.success("Logo uploadé ! Déplacez-le sur l'image pour le positionner.");
        }
        setIsProcessing(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    if (!uploadedLogo) return;
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !previewRef.current) return;
    
    const rect = previewRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setLogoPosition({
      x: Math.max(10, Math.min(90, x)),
      y: Math.max(10, Math.min(90, y))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (!previewRef.current) return;
    const touch = e.touches[0];
    const rect = previewRef.current.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setLogoPosition({
      x: Math.max(10, Math.min(90, x)),
      y: Math.max(10, Math.min(90, y))
    });
  };

  const resetLogo = () => {
    setUploadedLogo(null);
    setLogoPosition({ x: 50, y: 30 });
    setLogoSize([30]);
    setLogoRotation([0]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8C9D78] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="container-custom py-6">
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/boutique" className="text-stone-500 hover:text-[#8C9D78] flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Retour à la boutique
          </Link>
        </nav>
      </div>

      <div className="container-custom pb-20">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image with Logo Preview */}
          <div className="space-y-4">
            <div 
              ref={previewRef}
              className="relative aspect-square rounded-3xl overflow-hidden bg-white shadow-lg cursor-crosshair select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchMove={handleTouchMove}
            >
              <img
                src={selectedColor && product.color_images?.[selectedColor] ? product.color_images[selectedColor] : product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
                draggable={false}
              />
              
              {/* Logo Overlay */}
              {uploadedLogo && (
                <div 
                  className="absolute pointer-events-none transition-all duration-75"
                  style={{
                    left: `${logoPosition.x}%`,
                    top: `${logoPosition.y}%`,
                    transform: `translate(-50%, -50%) rotate(${logoRotation[0]}deg)`,
                    width: `${logoSize[0]}%`,
                    maxWidth: '60%'
                  }}
                >
                  <img 
                    src={uploadedLogo} 
                    alt="Votre logo" 
                    className="w-full h-auto drop-shadow-lg"
                  />
                </div>
              )}

              {/* Processing indicator */}
              {isProcessing && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-[#8C9D78] animate-spin mx-auto mb-3" />
                    <p className="text-[#1D3557] font-medium">Suppression du fond...</p>
                  </div>
                </div>
              )}

              {/* Upload hint overlay */}
              {!uploadedLogo && product.customizable && (
                <div className="absolute inset-0 bg-gradient-to-t from-[#1D3557]/60 via-transparent to-transparent flex items-end justify-center pb-8 opacity-0 hover:opacity-100 transition-opacity">
                  <span className="text-white font-medium flex items-center gap-2 bg-[#8C9D78] px-4 py-2 rounded-full">
                    <Upload className="w-4 h-4" />
                    Uploadez votre logo ci-dessous
                  </span>
                </div>
              )}

              {/* Dragging indicator */}
              {uploadedLogo && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#1D3557]/80 text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
                  <Move className="w-4 h-4" />
                  Cliquez et glissez pour repositionner
                </div>
              )}
            </div>

            {/* Color image thumbnails */}
            {product.color_images && Object.keys(product.color_images).length > 0 && (
              <div className="flex gap-2 flex-wrap" data-testid="color-thumbnails">
                {product.colors?.map((color, i) => {
                  const imgUrl = product.color_images[color];
                  if (!imgUrl) return null;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedColor(color)}
                      className={`relative w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${selectedColor === color ? "border-[#8C9D78] ring-2 ring-[#8C9D78]/30" : "border-stone-200 hover:border-stone-300"}`}
                      data-testid={`color-thumb-${color}`}
                    >
                      <img src={imgUrl} alt={color} className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] text-center py-0.5 truncate">{color}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Logo Upload & Controls */}
            {product.customizable && (
              <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#1D3557] flex items-center gap-2">
                    <Palette className="w-5 h-5 text-[#8C9D78]" />
                    Personnalisez avec votre logo
                  </h3>
                  {uploadedLogo && (
                    <Button onClick={resetLogo} variant="ghost" size="sm" className="text-stone-500">
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Réinitialiser
                    </Button>
                  )}
                </div>

                {!uploadedLogo ? (
                  <label className="block">
                    <div className="border-2 border-dashed border-[#8C9D78]/50 rounded-xl p-8 text-center cursor-pointer hover:border-[#8C9D78] hover:bg-[#8C9D78]/5 transition-all">
                      <Upload className="w-10 h-10 text-[#8C9D78] mx-auto mb-3" />
                      <p className="font-medium text-[#1D3557] mb-1">Cliquez pour uploader votre logo</p>
                      <p className="text-sm text-stone-500">PNG, JPG ou SVG (max 5MB)</p>
                    </div>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                  </label>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-3 bg-[#8C9D78]/10 rounded-xl">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-white border border-stone-200 flex-shrink-0 flex items-center justify-center">
                        <img 
                          src={uploadedLogo} 
                          alt="Logo" 
                          className="max-w-full max-h-full object-contain"
                          style={{ transform: `rotate(${logoRotation[0]}deg)` }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1D3557] flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[#8C9D78]" />
                          Logo traité (fond supprimé)
                        </p>
                        <p className="text-sm text-stone-500">Glissez sur l'image pour positionner</p>
                      </div>
                      <Button onClick={resetLogo} variant="ghost" size="icon" className="text-stone-400 hover:text-red-500">
                        <X className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Size slider */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-[#1D3557] flex items-center gap-2">
                          <ZoomIn className="w-4 h-4" />
                          Taille du logo
                        </label>
                        <span className="text-sm text-stone-500">{logoSize[0]}%</span>
                      </div>
                      <Slider
                        value={logoSize}
                        onValueChange={setLogoSize}
                        min={10}
                        max={60}
                        step={5}
                        className="cursor-pointer"
                        data-testid="logo-size-slider"
                      />
                    </div>

                    {/* Rotation slider */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-[#1D3557] flex items-center gap-2">
                          <RotateCw className="w-4 h-4" />
                          Rotation
                        </label>
                        <span className="text-sm text-stone-500">{logoRotation[0]}°</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setLogoRotation([logoRotation[0] - 15])}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Slider
                          value={logoRotation}
                          onValueChange={setLogoRotation}
                          min={-180}
                          max={180}
                          step={5}
                          className="cursor-pointer flex-1"
                          data-testid="logo-rotation-slider"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setLogoRotation([logoRotation[0] + 15])}
                        >
                          <RotateCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Reset rotation button */}
                    {logoRotation[0] !== 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-stone-500"
                        onClick={() => setLogoRotation([0])}
                      >
                        Réinitialiser la rotation
                      </Button>
                    )}
                  </div>
                )}

                {/* BAT Notice */}
                <div className="flex items-start gap-3 p-4 bg-[#1D3557]/5 rounded-xl border border-[#1D3557]/10">
                  <CheckCircle2 className="w-5 h-5 text-[#1D3557] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#1D3557] text-sm">Validation avant fabrication</p>
                    <p className="text-xs text-stone-600 mt-1">
                      Un BAT (Bon à Tirer) vous sera toujours envoyé pour validation avant le lancement de la production.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                {product.customizable && (
                  <Badge className="bg-[#8C9D78] text-white">
                    <Palette className="w-3 h-3 mr-1" />
                    Personnalisable
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }} data-testid="product-name">
                {product.name}
              </h1>
            </div>

            {/* Price display with tiers */}
            {product.pricing_tiers?.length > 0 ? (
              <div>
                <p className="text-xs text-[#C65D47] font-medium mb-1">Prix de vente indicatif à partir de</p>
                <span className="text-4xl font-bold text-[#8C9D78]" data-testid="product-price">
                  {formatPrice(getUnitPrice())}
                </span>
                <span className="text-stone-500 ml-2">/ unité HT</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-4">
                <span className="text-4xl font-bold text-[#8C9D78]" data-testid="product-price">
                  {formatPrice(product.price)}
                </span>
                <span className="text-stone-500">/ unité</span>
              </div>
            )}

            <p className="text-lg text-stone-600 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Colors */}
            {product.colors?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#1D3557] mb-2">Couleur</label>
                <div className="flex flex-wrap gap-2" data-testid="color-selector">
                  {product.colors.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${selectedColor === color ? "border-[#8C9D78] bg-[#8C9D78]/10 text-[#1D3557]" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`color-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.sizes?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[#1D3557] mb-2">Taille</label>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {product.sizes.map((size, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 rounded-xl text-sm font-medium border-2 transition-all ${selectedSize === size ? "border-[#8C9D78] bg-[#8C9D78] text-white" : "border-stone-200 text-stone-600 hover:border-stone-300"}`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-stone-100 rounded-full text-sm text-stone-600">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Pricing Tiers Table */}
            {product.pricing_tiers?.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="pricing-tiers-table">
                <div className="px-5 py-3 bg-[#1D3557]/5 border-b border-stone-200">
                  <h3 className="text-sm font-semibold text-[#1D3557]">Tarifs dégressifs par quantité</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {product.pricing_tiers.map((tier, i) => {
                    const isActive = quantity >= tier.min_qty && quantity <= tier.max_qty;
                    return (
                      <div key={i} className={`grid grid-cols-3 px-5 py-3 text-sm ${isActive ? "bg-[#8C9D78]/10 font-semibold" : ""}`}>
                        <span className="text-stone-600">{tier.min_qty} - {tier.max_qty} pcs</span>
                        <span className={isActive ? "text-[#8C9D78]" : "text-[#1D3557]"}>{formatPrice(tier.price)} HT</span>
                        {isActive && <span className="text-[#8C9D78] text-right">Palier actif</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity & Customization */}
            <div className="space-y-4 pt-4 border-t border-stone-200">
              <div>
                <label className="block text-sm font-medium text-[#1D3557] mb-2">
                  Quantité (min. {product.min_quantity} unités)
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-stone-300 rounded-full overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(product.min_quantity, quantity - 1))}
                      className="p-3 hover:bg-stone-50 transition-colors"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <Input
                      type="number"
                      min={product.min_quantity}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(product.min_quantity, parseInt(e.target.value) || product.min_quantity))}
                      className="w-20 text-center border-0 focus:ring-0"
                      data-testid="quantity-input"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-stone-50 transition-colors"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-stone-600">
                    Total: <span className="font-bold text-[#1D3557] text-xl">{formatPrice(getUnitPrice() * quantity)}</span>
                  </div>
                </div>
              </div>

              {/* 50% Deposit Info */}
              <div className="p-4 bg-[#8C9D78]/10 rounded-xl border border-[#8C9D78]/20" data-testid="deposit-info">
                <p className="text-sm font-semibold text-[#1D3557] mb-1">Paiement en 2 fois</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-stone-500">Acompte (50%)</p>
                    <p className="text-lg font-bold text-[#8C9D78]">{formatPrice(getUnitPrice() * quantity * 0.5)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-stone-500">Solde à la livraison</p>
                    <p className="text-lg font-bold text-[#1D3557]">{formatPrice(getUnitPrice() * quantity * 0.5)}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D3557] mb-2">
                  Instructions supplémentaires
                </label>
                <Textarea
                  placeholder="Couleur du logo, emplacement préféré, texte à ajouter..."
                  value={customization}
                  onChange={(e) => setCustomization(e.target.value)}
                  className="resize-none rounded-xl"
                  rows={3}
                  data-testid="customization-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleAddToCart}
                className="flex-1 bg-[#8C9D78] hover:bg-[#7A8B69] rounded-full h-14 text-lg font-semibold shadow-lg"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Ajouter au panier
              </Button>
              <Button variant="outline" size="icon" className="h-14 w-14 rounded-full border-stone-300">
                <Heart className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="icon" className="h-14 w-14 rounded-full border-stone-300">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-stone-200">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#8C9D78]/20 flex items-center justify-center mx-auto mb-2">
                  <Leaf className="w-6 h-6 text-[#8C9D78]" />
                </div>
                <p className="text-sm font-medium text-[#1D3557]">Éco-responsable</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#1D3557]/20 flex items-center justify-center mx-auto mb-2">
                  <Truck className="w-6 h-6 text-[#1D3557]" />
                </div>
                <p className="text-sm font-medium text-[#1D3557]">Livraison Corse & France</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#C65D47]/20 flex items-center justify-center mx-auto mb-2">
                  <Shield className="w-6 h-6 text-[#C65D47]" />
                </div>
                <p className="text-sm font-medium text-[#1D3557]">Qualité garantie</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="customization" className="mt-16">
          <TabsList className="w-full justify-start border-b border-stone-200 bg-transparent h-auto p-0 space-x-8">
            <TabsTrigger
              value="customization"
              className="pb-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#8C9D78] data-[state=active]:text-[#8C9D78] data-[state=active]:shadow-none"
            >
              Personnalisation
            </TabsTrigger>
            <TabsTrigger
              value="details"
              className="pb-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#8C9D78] data-[state=active]:text-[#8C9D78] data-[state=active]:shadow-none"
            >
              Caractéristiques
            </TabsTrigger>
            <TabsTrigger
              value="delivery"
              className="pb-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#8C9D78] data-[state=active]:text-[#8C9D78] data-[state=active]:shadow-none"
            >
              Livraison
            </TabsTrigger>
          </TabsList>
          <TabsContent value="customization" className="pt-8">
            <div className="prose prose-stone max-w-none">
              <h3>Techniques de personnalisation disponibles</h3>
              <div className="grid md:grid-cols-2 gap-6 not-prose mt-4">
                {[
                  { name: "Sérigraphie", desc: "Idéale pour les grandes quantités, couleurs vives et durables" },
                  { name: "Broderie", desc: "Rendu premium, parfait pour les vêtements et textiles" },
                  { name: "Gravure laser", desc: "Élégant et permanent sur métal, bois et verre" },
                  { name: "Impression numérique", desc: "Haute définition, idéale pour les logos complexes" },
                ].map((tech, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-stone-50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-[#8C9D78] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1D3557]">{tech.name}</p>
                      <p className="text-sm text-stone-600">{tech.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6">
                Notre équipe vous accompagne dans le choix de la technique la plus adaptée à votre projet.
                Uploadez votre logo ci-dessus pour visualiser le rendu !
              </p>
              <div className="not-prose mt-6 p-4 bg-[#8C9D78]/10 rounded-xl border border-[#8C9D78]/20">
                <p className="font-semibold text-[#1D3557] flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#8C9D78]" />
                  Validation garantie avant fabrication
                </p>
                <p className="text-sm text-stone-600 mt-2">
                  Un <strong>BAT (Bon à Tirer)</strong> vous sera systématiquement envoyé pour validation avant le lancement de la production. 
                  Vous aurez ainsi l'opportunité de vérifier et d'approuver le rendu final de votre personnalisation.
                </p>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="details" className="pt-8">
            <div className="prose prose-stone max-w-none">
              <h3>Caractéristiques du produit</h3>
              <ul>
                <li>Référence: {product.sku || "N/A"}</li>
                <li>Quantité minimum: {product.min_quantity} unités</li>
                <li>Stock disponible: {product.stock} unités</li>
                <li>Personnalisable: {product.customizable ? "Oui" : "Non"}</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="delivery" className="pt-8">
            <div className="prose prose-stone max-w-none">
              <h3>Informations de livraison</h3>
              <p>Livraison sous 2 à 4 semaines après validation de votre commande et du BAT (Bon à Tirer).</p>
              <p><strong>Zones de livraison :</strong> France métropolitaine et Corse.</p>
              <p>Pour les autres destinations, contactez-nous pour un devis personnalisé.</p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold text-[#1D3557] mb-8" style={{ fontFamily: 'Fraunces, serif' }}>
              Produits similaires
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProducts.map((p) => (
                <Link key={p.id} to={`/boutique/${p.id}`} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
                  <div className="aspect-square overflow-hidden bg-stone-100">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-[#1D3557] group-hover:text-[#8C9D78]">{p.name}</h3>
                    <p className="text-lg font-bold text-[#8C9D78]">{formatPrice(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
